import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, Building2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationType: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const organizationTypes = [
    'Federation',
    'Club',
    'Academy',
    'School/College',
    'Facility Operator',
    'Sponsor/Corporate',
    'Government Agency',
    'Individual/Athlete'
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await signUp(formData.email, formData.password, {
      first_name: formData.firstName,
      last_name: formData.lastName,
      organization_type: formData.organizationType
    });
    
    if (result.success) {
      toast.success(result.message);
      // Redirect to onboarding after email confirmation
      navigate('/login', { state: { redirectTo: '/onboarding' } });
    } else {
      toast.error(result.error || 'Signup failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:block w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1200&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="absolute inset-0 bg-secondary/10" />
        <div className="absolute top-12 left-12 right-12">
          <div className="glass p-8 rounded-xl">
            <h2 className="font-heading text-2xl font-bold text-white mb-4">Join Africa's Sports Community</h2>
            <ul className="space-y-3 font-body text-white/80">
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-primary text-sm">✓</span>
                </div>
                Manage events and tournaments
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-primary text-sm">✓</span>
                </div>
                Digital ticketing and accreditation
              </li>
              <li className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-primary text-sm">✓</span>
                </div>
                Connect with sports organizations
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 py-12 overflow-y-auto">
        <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white mb-8 w-fit" data-testid="back-home">
          <ArrowLeft size={18} />
          <span className="font-body text-sm">Back to Home</span>
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md"
        >
          <Link to="/" className="mb-6 inline-block">
            <span className="font-heading text-3xl font-bold">
              <span className="text-primary">MICHEZO</span>
              <span className="text-secondary">NET</span>
            </span>
          </Link>
          
          <h1 className="font-heading text-4xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-white/60 font-body mb-6">Join the digital sports ecosystem</p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/80 font-body text-sm mb-2">First Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full bg-accent/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md h-12 pl-12 pr-4 text-white font-body outline-none transition-all"
                    placeholder="John"
                    required
                    data-testid="signup-firstname"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white/80 font-body text-sm mb-2">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full bg-accent/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md h-12 px-4 text-white font-body outline-none transition-all"
                  placeholder="Doe"
                  required
                  data-testid="signup-lastname"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-white/80 font-body text-sm mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-accent/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md h-12 pl-12 pr-4 text-white font-body outline-none transition-all"
                  placeholder="you@example.com"
                  required
                  data-testid="signup-email"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-white/80 font-body text-sm mb-2">Organization Type</label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <select
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleChange}
                  className="w-full bg-accent/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md h-12 pl-12 pr-4 text-white font-body outline-none transition-all appearance-none cursor-pointer"
                  required
                  data-testid="signup-org-type"
                >
                  <option value="" className="bg-card">Select type...</option>
                  {organizationTypes.map(type => (
                    <option key={type} value={type} className="bg-card">{type}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-white/80 font-body text-sm mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full bg-accent/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md h-12 pl-12 pr-12 text-white font-body outline-none transition-all"
                  placeholder="Min. 6 characters"
                  required
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-white/80 font-body text-sm mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full bg-accent/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary/50 rounded-md h-12 pl-12 pr-4 text-white font-body outline-none transition-all"
                  placeholder="Confirm password"
                  required
                  data-testid="signup-confirm-password"
                />
              </div>
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 mt-1 rounded border-border bg-accent accent-primary" required />
              <span className="text-white/60 font-body text-sm">
                I agree to the{' '}
                <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              </span>
            </label>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="signup-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                'Create Account'
              )}
            </button>
          </form>
          
          <p className="text-white/60 font-body text-sm mt-6 text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SignupPage;
